import chr from "chroma-js";
import { ExtensionBadgeIcons } from "../../components/ExtensionBadges/ExtensionBadges";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { NavLink, useHistory } from "react-router-dom";
import { IconCheck, IconMinus, IconPencil, IconSettings, IconSparks } from "@humansignal/icons";
import { Userpic, Button, Tooltip, useToast } from "@humansignal/ui";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { ProjectTierBadge } from "../../components/ProjectTierBadge/ProjectTierBadge";
import { Pagination } from "../../components";
import { useAPI } from "../../providers/ApiProvider";
import { askConfirm } from "../../utils/ask";
import { cn } from "../../utils/bem";
import { absoluteURL } from "../../utils/helpers";
import { isCapacitor } from "../../utils/host";
import { ProjectStateChip } from "@humansignal/app-common";
import { useTranslation } from "../../i18n/LocaleProvider";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import { LogoThumb } from "../Home/OrglessHome";

const DEFAULT_CARD_COLORS = ["#FFFFFF", "#FDFDFC"];

// Stroke-style play / pause matching the other card action icons (pencil,
// settings): 24×24, fill none, stroke-width 2, round joins. The app's
// IconPlay / IconPause are FILLED glyphs and read too bold next to these.
const svgProps = {
  width: 24,
  height: 24,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};
const PlayStroke = () => (
  <svg {...svgProps}>
    <path d="M8 5.5v13a.7.7 0 0 0 1.06.6l10.5-6.5a.7.7 0 0 0 0-1.2L9.06 4.9A.7.7 0 0 0 8 5.5Z" />
  </svg>
);
const PauseStroke = () => (
  <svg {...svgProps}>
    <path d="M9 5v14M15 5v14" />
  </svg>
);

export const ProjectsList = ({
  projects,
  currentPage,
  totalItems,
  loadNextPage,
  pageSize,
  pageSizeOptions = [10, 30, 50, 100],
  onProjectUpdate,
  view = "grid",
}) => {
  return (
    <>
      <div className={cn("projects-page").elem("list").mod({ view }).toClassName()}>
        {projects.map((project) => (
          <ProjectCard key={project.id} project={project} onProjectUpdate={onProjectUpdate} view={view} />
        ))}
      </div>
      {/* Single bottom pagination — the top copy was removed in favor
          of the compact toolbar above the list (ws filter + view
          toggle). Page-size dropdown lives here so "N per page" still
          has a home. */}
      <div className={cn("projects-page").elem("pages").toClassName()}>
        <Pagination
          name="projects-list"
          label="Projects"
          page={currentPage}
          totalItems={totalItems}
          urlParamName="page"
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          onPageLoad={(page, pageSize) => loadNextPage(page, pageSize)}
        />
      </div>
    </>
  );
};

export const EmptyProjectsList = ({
  openModal,
  canCreateProjects = true,
  atProjectLimit = false,
  projectsMax,
  projectsCurrent,
  noActiveOrg = false,
}) => {
  const { t } = useTranslation();
  return (
    <div className={cn("empty-projects-page").toClassName()}>
      <img
        alt=""
        aria-hidden="true"
        className={cn("empty-projects-page").elem("heidi").toClassName()}
        src={absoluteURL("/static/brand/afriannotate/mark.png")}
      />
      {noActiveOrg ? (
        // The user's active organization is null — usually because
        // they were just removed from their only org. Show a
        // friendly "you're on your own" message instead of leaving
        // them staring at an empty list.
        <>
          <h1 className={cn("empty-projects-page").elem("header").toClassName()}>You're not in any organization</h1>
          <p>
            Your membership was removed or the organization is no longer available. Ask a manager or admin to invite you
            again — or contact your platform administrator.
          </p>
        </>
      ) : (
        <>
          <h1 className={cn("empty-projects-page").elem("header").toClassName()}>No projects here yet</h1>
          {isCapacitor() && (
            // Mobile hides projects whose owner hasn't enabled them for
            // phones — without this line, an owner whose projects all
            // have the toggle off reads the empty list as data loss.
            <p>
              Projects appear on mobile once "Allow on mobile" is turned on in the project's General settings (web or
              desktop).
            </p>
          )}
          {canCreateProjects ? (
            <>
              <p>{t("pages.projects.projects_list.create_one_and_start_labeling_your_data")}</p>
              <Button
                onClick={openModal}
                className="my-8"
                aria-label="Create new project"
                disabled={atProjectLimit}
                title={
                  atProjectLimit
                    ? `Project limit reached (${projectsCurrent}/${projectsMax}). Contact a platform administrator.`
                    : undefined
                }
              >
                Create Project
              </Button>
              {atProjectLimit && (
                <p style={{ color: "var(--color-negative-content, #b91c1c)", fontSize: "var(--font-size-label)" }}>
                  Project limit reached ({projectsCurrent}/{projectsMax}).
                </p>
              )}
            </>
          ) : (
            <p>{t("pages.projects.projects_list.ask_an_organization_admin_or_manager_to_")}</p>
          )}
        </>
      )}
    </div>
  );
};

const ProjectCard = ({ project, onProjectUpdate, view = "grid" }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const api = useAPI();
  const toast = useToast();
  const history = useHistory();
  const [publishing, setPublishing] = useState(false);
  // ``onlineStatus`` drives the disabled state of the Label icon
  // (and is forwarded to ProjectTierBadge, which disables Download
  // when the cloud is unreachable). The hook polls ``/api/version/``
  // every 12 s; reads the same flag as the menubar OfflinePill so
  // both pieces of UX agree on online/offline state.
  const onlineStatus = useOnlineStatus();
  const canManageProject =
    user?.is_staff ||
    ["OW", "AD", "MA"].includes(user?.active_organization_role) ||
    project?.created_by?.id === user?.id;

  // Has this project ever been published? Drives button copy —
  // first-time Publish vs Resume-after-pause. `published_at` is
  // null until the first successful publish lands on the server.
  const everPublished = !!project.published_at;

  const togglePublished = async (e, next) => {
    e.preventDefault();
    e.stopPropagation();
    if (publishing) return;
    if (!next) {
      const ok = await askConfirm({
        title: `Pause "${project.title}"?`,
        body: "Annotators and reviewers will lose access immediately. Anyone currently on the project will be sent back to the projects list. Already-submitted work is unaffected.",
        okText: "Pause",
        danger: true,
      });
      if (!ok) return;
    }
    setPublishing(true);
    const res = await api.callApi("updateProject", {
      params: { pk: project.id },
      body: { is_published: next },
    });
    setPublishing(false);
    if (!res || res.error) {
      toast.show({
        message: res?.error ?? (next ? "Couldn't publish project" : "Couldn't pause project"),
        type: "error",
      });
      return;
    }
    // Mirror the server's published_at semantics: first publish
    // stamps a timestamp, subsequent toggles leave it alone.
    const updates = { is_published: next };
    if (next && !everPublished) {
      updates.published_at = res?.published_at ?? new Date().toISOString();
    }
    const verb = next ? (everPublished ? "resumed" : "published") : "paused";
    toast.show({ message: `"${project.title}" ${verb}` });
    onProjectUpdate?.(project.id, updates);
  };
  const color = useMemo(() => {
    return DEFAULT_CARD_COLORS.includes(project.color) ? null : project.color;
  }, [project]);

  const projectColors = useMemo(() => {
    const textColor =
      color && chr(color).luminance() > 0.3
        ? "var(--color-neutral-inverted-content)"
        : "var(--color-neutral-inverted-content)"; // Determine text color based on luminance
    return color
      ? {
          "--header-color": color,
          "--background-color": chr(color).alpha(0.2).css(),
          "--text-color": textColor,
          "--border-color": chr(color).alpha(0.5).css(),
        }
      : {};
  }, [color]);

  return (
    <NavLink
      className={cn("projects-page").elem("link").mod({ view }).toClassName()}
      to={`/projects/${project.id}`}
      data-external
    >
      <div className={cn("project-card").mod({ colored: !!color, view }).toClassName()} style={projectColors}>
        <div className={cn("project-card").elem("header").toClassName()}>
          <div className={cn("project-card").elem("title").toClassName()}>
            <LogoThumb src={project.logo} title={project.title} />
            <div className={cn("project-card").elem("title-text-wrapper").toClassName()}>
              <Tooltip title={project.title ?? "New project"}>
                <div className={cn("project-card").elem("title-text").toClassName()}>
                  {project.title ?? "New project"}
                </div>
              </Tooltip>
            </div>

            {project.state && (
              <div className={cn("project-card").elem("state-chip").toClassName()}>
                <ProjectStateChip state={project.state} projectId={project.id} interactive={false} />
              </div>
            )}

            {/* Managers get a publish/pause ICON in the bottom action row
                (see below). Non-managers only ever see a static status
                badge here as a fallback for creator edge-cases. */}
            {!canManageProject && project.is_published === false ? (
              <div
                className={cn("project-card").elem("draft-badge").toClassName()}
                title={
                  everPublished
                    ? "Paused, hidden from annotators until a manager resumes it."
                    : "Not yet published, hidden from annotators."
                }
              >
                {everPublished ? "Paused" : "Not published"}
              </div>
            ) : null}
            {/* Labels an installed extension puts on the project
                (core/extensions.py: project_data, key ``badges``), as
                icons: a card has no room for the words, and the label
                is in the tooltip. */}
            <ExtensionBadgeIcons badges={project.extensions?.badges} />
          </div>
          <div className={cn("project-card").elem("summary").toClassName()}>
            <div className={cn("project-card").elem("annotation").toClassName()}>
              <div className={cn("project-card").elem("total").toClassName()}>
                {project.finished_task_number} / {project.task_number}
              </div>
              <div className={cn("project-card").elem("detail").toClassName()}>
                <div className={cn("project-card").elem("detail-item").mod({ type: "completed" }).toClassName()}>
                  <IconCheck className={cn("project-card").elem("icon").toClassName()} />
                  {project.total_annotations_number}
                </div>
                <div className={cn("project-card").elem("detail-item").mod({ type: "rejected" }).toClassName()}>
                  <IconMinus className={cn("project-card").elem("icon").toClassName()} />
                  {project.skipped_annotations_number}
                </div>
                <div className={cn("project-card").elem("detail-item").mod({ type: "predictions" }).toClassName()}>
                  <IconSparks className={cn("project-card").elem("icon").toClassName()} />
                  {project.total_predictions_number}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          className={cn("project-card").elem("description").toClassName()}
          style={
            project.description ? undefined : { color: "var(--color-neutral-content-subtler)", fontStyle: "italic" }
          }
        >
          {project.description || "No description provided."}
        </div>
        <div className={cn("project-card").elem("info").toClassName()}>
          <div className={cn("project-card").elem("created-date").toClassName()}>
            {/* A mirrored row can lack created_at (written from a narrower
                projection); ``format(new Date(undefined))`` throws
                RangeError: Invalid time value and takes the whole page
                into the error boundary. Guard it. */}
            {(() => {
              const d = project.created_at ? new Date(project.created_at) : null;
              return d && !Number.isNaN(d.getTime()) ? format(d, "dd MMM yyyy, HH:mm") : "";
            })()}
            {project.is_archived && (
              <span className={cn("project-card").elem("archived-pill").toClassName()}>Archived</span>
            )}
          </div>
          {/* Quick-action icons sit next to the owner avatar so the
              "who + what" live in one row at the bottom of the card.
              Must be <button>s (not <a>): the whole card is wrapped in
              a NavLink, and nested <a>-in-<a> is invalid HTML — the
              browser silently ignores the inner anchor, making the
              icons un-clickable. Using buttons + history.push sidesteps
              the nesting rule. */}
          <div
            className={cn("project-card").elem("owner-row").toClassName()}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            {(() => {
              // Label button: disabled when the cloud is unreachable
              // AND this project hasn't been downloaded for offline.
              // ``cloudUp`` comes from ``useOnlineStatus`` (called
              // up at the parent ``ProjectCard`` body); ``downloaded``
              // reads the same localStorage flag ``ProjectTierBadge``
              // sets on a successful pull.
              const cloudUp = onlineStatus;
              const cloudId = project.cloud_id ?? project.id;
              const downloaded =
                typeof window !== "undefined" &&
                !!window.localStorage.getItem(`afri:mobile:project:${cloudId}:downloaded`);
              const labelDisabled = !cloudUp && !downloaded;
              const tipKey = labelDisabled
                ? "pages.projects.projects_list.label_tasks_offline_disabled"
                : "pages.projects.projects_list.label_tasks";
              return (
                <Tooltip title={t(tipKey)}>
                  <button
                    type="button"
                    className={cn("project-card").elem("action-btn").mod({ disabled: labelDisabled }).toClassName()}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (labelDisabled) return;
                      history.push(`/projects/${project.id}/data?labeling=1`);
                    }}
                    aria-disabled={labelDisabled}
                    aria-label={t(tipKey)}
                  >
                    <IconPencil />
                  </button>
                </Tooltip>
              );
            })()}
            {/* Single icon-only affordance for offline state. Reads
                ``Project.local_sync_tier`` + per-user OfflineProjectPref
                and (when togglable) toggles via /api/afri/sync/
                set_offline_pref/. Replaces the prior dual setup of a
                separate "download" button + a tier pill. Hidden on
                the plain web build. */}
            <ProjectTierBadge project={project} onChange={onProjectUpdate} />
            {/* Publish / pause as an icon, in the bottom action row.
                Unpublished → publish or resume (check); live → pause (minus). */}
            {canManageProject &&
              !project.is_archived &&
              (() => {
                const unpublished = project.is_published === false;
                const label = unpublished ? (everPublished ? "Resume project" : "Publish project") : "Pause project";
                return (
                  <Tooltip title={publishing ? "Working…" : label}>
                    <button
                      type="button"
                      className={cn("project-card").elem("action-btn").mod({ disabled: publishing }).toClassName()}
                      onClick={(e) => togglePublished(e, unpublished)}
                      disabled={publishing}
                      aria-label={label}
                    >
                      {unpublished ? <PlayStroke /> : <PauseStroke />}
                    </button>
                  </Tooltip>
                );
              })()}
            {canManageProject && (
              <Tooltip title={t("pages.projects.projects_list.project_settings")}>
                <button
                  type="button"
                  className={cn("project-card").elem("action-btn").toClassName()}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    history.push(`/projects/${project.id}/settings`);
                  }}
                  aria-label={t("pages.projects.projects_list.project_settings")}
                >
                  <IconSettings />
                </button>
              </Tooltip>
            )}
            <div className={cn("project-card").elem("created-by").toClassName()}>
              <Userpic src="#" user={project.created_by} showUsernameTooltip />
            </div>
          </div>
        </div>
      </div>
    </NavLink>
  );
};
